import React, { useState, useEffect, Suspense } from 'react';

// Lazy load ReactPlayer to reduce initial bundle size
const ReactPlayer = React.lazy(() => import('react-player'));

// Loading component for ReactPlayer
const PlayerLoader = ({ className = "" }) => (
  <div className={`${className} flex items-center justify-center bg-gray-900`}>
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
      <p className="text-white text-sm">Loading player...</p>
    </div>
  </div>
);

const LazyReactPlayer = ({ 
  url, 
  width = "100%", 
  height = "100%", 
  playing = false, 
  muted = false, 
  config = {}, 
  className = "",
  onReady = null,
  onStart = null,
  onPlay = null,
  onPause = null,
  onError = null,
  ...props 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [, setHasLoaded] = useState(false);

  useEffect(() => {
    // Only start loading when component is about to be visible
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100); // Small delay to avoid blocking initial render

    return () => clearTimeout(timer);
  }, []);

  const handleReady = () => {
    setHasLoaded(true);
    onReady?.();
  };

  if (!isVisible) {
    return (
      <div 
        className={`${className} flex items-center justify-center bg-gray-900`}
        style={{ width, height }}
      >
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-2 rounded bg-gray-700 flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 5v10l8-5-8-5z"/>
            </svg>
          </div>
          <p className="text-white text-sm">Preparing player...</p>
        </div>
      </div>
    );
  }

  // Build props object, only including callbacks if they're functions
  const playerProps = {
    url,
    width,
    height,
    playing,
    muted,
    config,
    className,
    onReady: handleReady,
    ...(typeof onStart === 'function' && { onStart }),
    ...(typeof onPlay === 'function' && { onPlay }),
    ...(typeof onPause === 'function' && { onPause }),
    ...(typeof onError === 'function' && { onError }),
    ...props
  };

  return (
    <Suspense fallback={<PlayerLoader className={className} style={{ width, height }} />}>
      <ReactPlayer {...playerProps} />
    </Suspense>
  );
};

export default LazyReactPlayer;
